import React, {
  useCallback,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';
import {
  Headline,
} from 'anf-core-react';
import ProductExchangeCard from '../product-exchange-card/product-exchange-card';
import TmntText from '../tmnt-text/tmnt-text';
import productsShape from './products-shape';

export default function ProductCategoryListLegacy({
  category,
  products,
  selectedProducts,
  selectedProductComments,
  showEligible,
  returnReasonCodeList,
  handleSelectedProduct,
  handleUpdateComments,
  eligibility,
}) {
  const canReturnProduct = useCallback((product) => !product.finalSaleItem
    && (!product.returnDetails || product.returnable)
    && product.returnable, []);

  const getProductSortPosition = useCallback((product) => {
    if (!product.finalSaleItem && !product.returnDetails && !product.returnable) return 1;
    if (product.finalSaleItem) return 2;
    if (product.returnDetails && !product.returnable) return 3;
    return 0;
  }, []);

  const productMap = useMemo(() => products.filter(
    (product) => canReturnProduct(product) === showEligible,
  ), [products, canReturnProduct, showEligible]);

  const productList = useMemo(() => productMap.sort(
    (a, b) => {
      const sortA = getProductSortPosition(a);
      const sortB = getProductSortPosition(b);

      if (sortA !== sortB) return sortA - sortB;

      return (a.name || '').localeCompare(b.name);
    },
  ).map((product) => {
    const selectedProduct = selectedProducts.find(
      (p) => p.orderLineKey === product.orderLineKey,
    );

    return (
      <ProductExchangeCard
        product={product}
        exchange={selectedProduct?.exchange}
        exchangeReasons={returnReasonCodeList}
        key={`product-card-${product.primeLineKey}`}
        checked={!!selectedProduct}
        onChange={handleSelectedProduct}
        selectedProductComments={selectedProductComments}
        updateComments={handleUpdateComments}
      />
    );
  }), [
    productMap,
    selectedProducts,
    selectedProductComments,
    handleSelectedProduct,
    handleUpdateComments,
  ]);

  if (!productMap.length) return null;

  return (
    <div className="product-category-list" data-testid={`product-category-list-legacy-${eligibility}`}>
      {category && (
      <Headline
        align="center"
        theme="default"
      >
        <TmntText k={category} />
      </Headline>
      )}
      {productList}
    </div>
  );
}

ProductCategoryListLegacy.propTypes = {
  products: productsShape.isRequired,
  selectedProducts: productsShape.isRequired,
  returnReasonCodeList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  selectedProductComments: PropTypes.shape({
    exchangeReason: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
    exchangeSecondaryReason: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      type: PropTypes.string,
    }),
    exchangeComment: PropTypes.string,
  }).isRequired,
  handleSelectedProduct: PropTypes.func.isRequired,
  handleUpdateComments: PropTypes.func.isRequired,
  category: PropTypes.string,
  showEligible: PropTypes.bool,
  eligibility: PropTypes.string,
};
